import z from "zod";

// Known notification types. The registry is code-owned on the backend (the
// column has no CHECK), so an old client can receive a type it doesn't know —
// keep the union open and fall back to the notification's fallbackTitle/body.
const NotificationTypeValues = [
  "billing.payment_failed",
  "billing.subscription.trial_ending",
  "billing.subscription.canceled",
  "billing.subscription.downgraded",
  "billing.subscription.expired",
  "billing.credits.threshold",
  "billing.auto_topup.failed",
  "billing.auto_topup.cap_reached",
  "activity.prompt.completed",
  "activity.prompt.failed",
  "integrations.slack.update_needed",
] as const;

const NotificationTypeSchema = z.enum(NotificationTypeValues).or(z.string());

// Display strings are rendered client-side from type + payload; the shape of
// payload varies by type (activity → chat/message/project ids, threshold →
// percent, payment_failed → amount, lifecycle → dates/plan names).
export const NotificationSchema = z.object({
  id: z.string(),
  type: NotificationTypeSchema,
  createdAt: z.string(),
  readAt: z.string().nullable(),
  organizationId: z.string().nullish(),
  payload: z.record(z.string(), z.unknown()),
  fallbackTitle: z.string(),
  fallbackBody: z.string(),
});
export type Notification = z.infer<typeof NotificationSchema>;

export const NotificationsListResponseSchema = z.object({
  notifications: z
    .array(NotificationSchema)
    .nullable()
    .transform((v) => v ?? []),
  nextCursor: z.string().nullable(),
});

export const NotificationsListParamsSchema = z.object({
  cursor: z.string().optional(),
  limit: z.number().optional(),
});

export const NotificationIdParamsSchema = z.object({
  notificationId: z.string(),
});

export const UnreadCountResponseSchema = z.object({
  count: z.number(),
});

// SSE data frame payload (base64-decoded JSON).
export const NotificationStreamPayloadSchema = z.object({
  notification: NotificationSchema,
});

export const BillingAlertThresholdsResponseSchema = z.object({
  thresholds: z
    .array(z.number())
    .nullable()
    .transform((v) => v ?? []),
});

// Full replace: an empty list resets to the backend defaults (80, 100).
export const UpdateBillingAlertThresholdsInputSchema = z.object({
  thresholds: z.array(z.number()),
});

// A contact is either an org member (user fields populated) or an external
// email (user fields null). The email is the only field common to both.
export const BillingContactSchema = z.object({
  userId: z.string().nullable(),
  displayName: z.string().nullable(),
  email: z.string(),
  avatarUrl: z.string().nullable(),
});
export type BillingContact = z.infer<typeof BillingContactSchema>;

export const BillingContactsResponseSchema = z.object({
  contacts: z
    .array(BillingContactSchema)
    .nullable()
    .transform((v) => v ?? []),
});

// Full replace of the contact set. Each entry references either a member by id
// or an external address by email, never both.
const UpdateBillingContactEntrySchema = z.union([z.object({ userId: z.string() }), z.object({ email: z.string() })]);

export const UpdateBillingContactsInputSchema = z.object({
  contacts: z.array(UpdateBillingContactEntrySchema),
});

// Members always carry user fields; only the avatar can be absent.
const BillingMemberSchema = z.object({
  userId: z.string(),
  displayName: z.string(),
  email: z.string(),
  avatarUrl: z.string().nullable(),
  role: z.string(),
});

export const BillingMembersResponseSchema = z.object({
  members: z
    .array(BillingMemberSchema)
    .nullable()
    .transform((v) => v ?? []),
});

// One in-flight prompt from the server-side running registry: the snapshot a
// fresh page load merges with its own (empty) streaming state.
export const RunningPromptSchema = z.object({
  chatId: z.string(),
  projectId: z.string(),
  title: z.string(),
  preview: z.string(),
  startedAt: z.string(),
});
export type RunningPrompt = z.infer<typeof RunningPromptSchema>;

export const RunningPromptsResponseSchema = z.object({
  running: z
    .array(RunningPromptSchema)
    .nullable()
    .transform((v) => v ?? []),
});
